"use client";

import { useRef, useEffect, useState } from "react";
import { Avatar } from "./ui/Avatar";
import { RichTextInput, type RichTextInputHandle } from "./RichTextInput";
import { ComposerLinkPreview } from "./ComposerLinkPreview";
import { AttachmentTray, AddAttachmentButton } from "./AttachmentTray";
import { errorText } from "@/lib/errors";
import { useAttachments } from "@/lib/hooks/useAttachments";
import { useCurrentUser } from "@/lib/hooks/useCurrentUser";
import { useCreateComment } from "@/lib/hooks/useCreateComment";
import { enterThread } from "@/lib/hooks/useThreadFocus";
import { useDraft } from "@/lib/hooks/useDraft";
import { findFirstUrl } from "@/lib/linkDetect";
import { SubmitHint, SubmitTooltip } from "./SubmitShortcut";

export function CommentComposer({
  postId,
  parentId,
  replyTo,
  groupId,
  groupSlug,
  onSuccess,
  onCancel,
  currentUserId,
  value,
  onChange,
}: {
  /** The post the thread hangs off — what a successful write invalidates. */
  postId: number;
  /**
   * Who this is a reply TO — the post itself for a top-level comment, a
   * comment's id for a reply. THE PARENT IS THE URL (C4), so this is the path
   * segment, not a field in the body.
   */
  parentId?: number;
  /**
   * The author of the comment being answered, when this box was opened by
   * Svar (OSO-596). Their @-tag is put in the empty box for the writer, who is
   * then free to delete it — it is text, not a setting.
   *
   * OMITTED when there is nobody to tag: an anonymous comment, a deleted
   * account, or your own comment. The notification does not depend on it
   * either way — `reply` goes to the parent's author whether or not the body
   * names them, and the backend collapses the two into one when it does.
   */
  replyTo?: { id: number; name: string } | null;
  groupId?: number;
  /** The group's slug, for the @alle capability lookup (OSO-681). */
  groupSlug?: string;
  onSuccess?: () => void;
  onCancel?: () => void;
  currentUserId?: number | null;
  value?: string;
  onChange?: (v: string) => void;
}) {
  // If parent manages state (value/onChange), use those; otherwise use an
  // auto-saved draft scoped to this post (and parent comment, for replies).
  const mentionRef = useRef<RichTextInputHandle>(null);
  const { data: currentUser } = useCurrentUser();
  const target = parentId ?? postId;
  const controlled = value !== undefined && onChange !== undefined;
  const draftScope = controlled ? null : `comment:${postId}${parentId != null ? `:${parentId}` : ""}`;
  const draft = useDraft(draftScope);
  const body = value ?? draft.value;
  const write = onChange ?? draft.setValue;
  const setBody = (next: string) => {
    enterThread(postId);
    write(next);
  };

  const files = useAttachments(
    groupId ? { type: "group", id: groupId } : { type: "avatar" }
  );

  /**
   * The link whose preview card the author closed (OSO-568). A comment draws
   * the same card as a post and had the same defect: the dismissal lived in
   * `ComposerLinkPreview` and never reached the server, so the published
   * comment previewed the link again.
   *
   * NOT in the draft. What survives navigation is the text; a card the author
   * closed and then walked away from is not worth a second storage key.
   */
  const [dismissedLink, setDismissedLink] = useState<string | null>(null);

  useEffect(() => {
    mentionRef.current?.focus();
  }, []);

  /**
   * THE @-TAG THE Svar BUTTON PUTS THERE (OSO-596) — once, into an empty box.
   *
   * ONCE: it is a starting point for this reply, not something that comes back
   * and rewrites what the author has since typed or deleted.
   *
   * EMPTY: `prefillMention` refuses a box with anything in it, which covers
   * the controlled case — but a `useDraft` box is empty on mount whether or
   * not there is a draft, because hydration is an effect. Waiting for
   * `draft.hydrated` is what keeps the prefill from eating a saved reply.
   *
   * AND `body` DOES NOT MOVE. The chip lives in the editor, not in this
   * component's state, so the tag is never written to the draft and `canSubmit`
   * stays false until the author types something of their own — a box holding
   * nothing but the name of the person you are answering is not a reply. The
   * text still reaches the server: `submit` reads `getRaw()` off the editor,
   * which serializes the chip to `@[id]`.
   */
  const prefilled = useRef(false);
  useEffect(() => {
    if (prefilled.current || !replyTo) return;
    if (!controlled && !draft.hydrated) return;
    prefilled.current = true;
    mentionRef.current?.prefillMention({ userId: replyTo.id, displayName: replyTo.name });
  }, [replyTo, controlled, draft.hydrated]);

  /**
   * THE SAME WRITE THE CARD'S OWN BOX MAKES, and the reason it is shared: this
   * used to invalidate two query keys and draw nothing, so a reply written in
   * the feed reached the database and then waited for a poll to notice it
   * (OSO-563). The hook splices the server's own row into the one thread cache
   * and draws a placeholder while it is in flight.
   */
  const createReply = useCreateComment({ rootPostId: postId, postedTo: target });

  const submit = () => {
    enterThread(postId);
    const raw = mentionRef.current?.getRaw() ?? body;
    const tray = files.attachments;
    createReply.mutate(
      {
        bodyMarkdown: raw,
        attachments: files.uploadIds,
        trayAttachments: tray,
        // Only when it is still the link being sent — see `PostComposer`.
        dismissedLinkPreviewUrl:
          dismissedLink && findFirstUrl(raw) === dismissedLink ? dismissedLink : null,
      },
      {
        // The composer closes only once the reply is really written. While the
        // request is in flight the placeholder is already in the thread above,
        // and if it fails the box is still on screen to say so (OSO-443) —
        // with the text AND the pictures back in it, so pressing send again
        // sends what was written rather than a copy missing its photo.
        onSuccess: () => onSuccess?.(),
        onError: () => {
          setBody(raw);
          files.restore(tray);
        },
      },
    );
    setBody("");
    draft.clear();
    setDismissedLink(null);
    mentionRef.current?.clearMentions();
    files.reset();
  };

  const canSubmit =
    (body.trim().length > 0 || files.hasContent) &&
    !files.isUploading &&
    !createReply.isPending;

  const handleSubmit = () => {
    if (canSubmit) submit();
  };

  return (
    <div className="flex items-start gap-2 pt-2">
      {currentUserId != null && currentUser && (
        <Avatar url={currentUser.avatar_template?.replace("{size}", "48")} name={currentUser.name} size="sm" />
      )}
      <form
        className="flex-1 group"
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit();
        }}
      >
        <div className="relative">
          <RichTextInput
            ref={mentionRef}
            ariaLabel="Skriv et svar"
            value={body}
            onChange={setBody}
            onSubmit={handleSubmit}
            onEscape={onCancel}
            onPasteFiles={files.addFiles}
            placeholder="Skriv et svar..."
            disabled={createReply.isPending}
            groupId={groupId}
            groupSlug={groupSlug}
            multiline
            hideFormattingIcon
            /* `leading-normal` is EXPLICIT (OSO-629 follow-up): `text-content`
               sets font-size only, and the conversion silently traded
               text-sm's 20px line for the inherited 1.5 — which is the right
               height (it is what the sent comment renders at in CommentItem)
               but was nobody's decision. At that height one line plus `py-2`
               stands 40px, so `rounded-[20px]` keeps the box a true pill and
               `bottom-1.5` centers the 28px send button in it. */
            className="w-full bg-muted rounded-[20px] px-4 py-2 pr-10 text-content leading-normal text-foreground focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:bg-surface transition-colors"
          />
          <SubmitTooltip label="Send svar" className="absolute right-2 bottom-1.5">
            <button
              type="submit"
              disabled={!canSubmit}
              aria-label="Send svar"
              className={`w-7 h-7 rounded-full flex items-center justify-center transition-colors ${
                !canSubmit
                  ? "text-subtle-foreground cursor-not-allowed"
                  : "bg-indigo-600 hover:bg-indigo-700 text-white"
              }`}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
              </svg>
            </button>
          </SubmitTooltip>
        </div>
        <SubmitHint value={body} />

        {/* Same rule as the post composer: the card follows the text */}
        <ComposerLinkPreview
          text={body}
          dismissed={dismissedLink}
          onDismiss={setDismissedLink}
        />

        {files.attachments.length > 0 && (
          <AttachmentTray
            attachments={files.attachments}
            onRemove={files.remove}
            onMove={files.move}
            size="sm"
          />
        )}
        {files.error && (
          <p className="mt-1 text-xs text-destructive">{files.error}</p>
        )}
        {/* Same gap the post composer had (OSO-443): a comment the server
            refused just never appeared, with nothing said. */}
        {createReply.isError && (
          <p role="alert" className="mt-1 text-xs text-destructive">
            {errorText(createReply.error, "Svaret kunne ikke sendes. Prøv igen.")}
          </p>
        )}
      </form>
      <AddAttachmentButton
        onFiles={files.addFiles}
        label="Tilføj billede til svar"
        className="shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-subtle-foreground hover:bg-muted hover:text-green-500 transition-colors"
      />
    </div>
  );
}
